/**
 * Content editor initialization — IIFE entry path.
 *
 * Sets up event handlers, message listeners, and editor lifecycle.
 * This module replaces the init/enableEditor/disableEditor/initEditor
 * section of original-musk-html-editor.js (lines 3326–4112).
 *
 * Architecture: EditorConcern pattern
 * ------------------------------------
 * Each reversible editing capability (event interceptors, interaction styles,
 * disabled-attr management, etc.) is encapsulated as an EditorConcern that
 * co-locates its enable/pause/resume/destroy logic. Adding a new editing
 * feature only requires writing one concern and pushing it into the array —
 * lifecycle functions (pause/resume/disable) automatically pick it up.
 */

import { MessageBridge } from './message-bridge'
import { markAllElements, markNewElements } from './editability'
import { HistoryManager } from './history'
import { InteractionDisabler } from './interaction'
import { ToolbarManager } from './toolbar'
import { FormulaManager } from './formula'
import { GeneralAIPanel } from './ai-panel'
import { ImagePanel } from './image-panel'
import { EditManager } from './edit-manager'
import { CONFIG, createFeatureConfig, type FeatureConfig } from './config'
import {
  log, getElementPath, isMathLiveEvent, describeEventTarget,
  markMathLivePointerDown, consumeMathLivePointerDownMark,
} from './utils'
import { findGeneralElement } from './ai-input'
import { cleanEditorAttributes, cleanEditorHead, cleanEditorRoot } from '../shared/cleanup'

// ========== EditorConcern interface ==========

interface EditorConcern {
  name: string,
  enable: () => void,
  pause: () => void,
  resume: () => void,
  destroy: () => void,
}

// ========== Module-level state ==========

let featureConfig: FeatureConfig = createFeatureConfig()
let isInitialized = false
let isEditorEnabled = false
let eventHandlers: Array<{
  element: EventTarget,
  event: string,
  handler: EventListener,
  options: boolean,
}> = []

const concerns: EditorConcern[] = []

// ========== Module-level instances ==========

const bridge = new MessageBridge()
const history = new HistoryManager(CONFIG.MAX_HISTORY, bridge)
const disabler = new InteractionDisabler()
const toolbar = new ToolbarManager(bridge, history, featureConfig)
const formulaManager = new FormulaManager(bridge, history, featureConfig)
const generalPanel = new GeneralAIPanel(bridge)
const imagePanel = new ImagePanel(bridge, history)
const editManager = new EditManager(toolbar, bridge, history, featureConfig)

// ========== cleanHtmlForSave ==========

/**
 * 清理 HTML,移除编辑器添加的属性和元素。
 * 使用 cloneNode 避免影响当前文档，返回完整的 HTML 文档（包括 <!DOCTYPE html>）。
 */
function cleanHtmlForSave(): string {
  const clonedDoc = document.cloneNode(true) as Document

  if (clonedDoc.head) {
    cleanEditorHead(clonedDoc.head)
  }

  if (clonedDoc.documentElement) {
    cleanEditorRoot(clonedDoc.documentElement)
  }

  if (clonedDoc.body) {
    cleanEditorAttributes(clonedDoc.body)
  }

  const fullHtml = clonedDoc.documentElement.outerHTML

  log('HTML 已清理', {
    originalLength: document.documentElement.outerHTML.length,
    cleanedLength: fullHtml.length,
    hasDoctype: document.doctype !== null,
  })

  if (document.doctype) {
    const doctypeString = `<!DOCTYPE ${document.doctype.name}>`
    return `${doctypeString}\n${fullHtml}`
  }

  return fullHtml
}

// ========== EditorConcern builders ==========

/**
 * 构建所有编辑关注点。每个 concern 将自己的 enable/pause/resume/destroy
 * 逻辑封装在一起，新增编辑功能只需添加新的 concern，无需修改生命周期函数。
 */
function buildConcerns(): EditorConcern[] {
  // ---- Concern 1: 面板管理 (toolbar / formula / general / image) ----
  const panelsConcern: EditorConcern = {
    name: 'panels',
    enable() { /* 面板按需创建，enable 时无需操作 */ },
    pause() {
      editManager.stopEditing()
      toolbar.hide()
      formulaManager.hide()
      generalPanel.hide()
      imagePanel.hide()
    },
    resume() { /* 面板按需创建，resume 时无需操作 */ },
    destroy() {
      editManager.stopEditing()
      toolbar.destroy()
      formulaManager.destroy()
      generalPanel.destroy()
      imagePanel.destroy()
    },
  }

  // ---- Concern 2: disabled 属性管理 ----
  const disabledAttrConcern: EditorConcern = {
    name: 'disabled-attr',
    enable() {
      document.querySelectorAll('[disabled]').forEach((el) => {
        if (
          el.querySelector(`[${CONFIG.EDITABLE_ATTR}="true"]`)
          || el.querySelector('[data-musk-element-id]')
        ) {
          el.setAttribute('data-musk-was-disabled', '')
          el.removeAttribute('disabled')
        }
      })
    },
    pause() {
      document.querySelectorAll('[data-musk-was-disabled]').forEach((el) => {
        el.setAttribute('disabled', '')
      })
    },
    resume() { this.enable() },
    destroy() {
      document.querySelectorAll('[data-musk-was-disabled]').forEach((el) => {
        el.setAttribute('disabled', '')
        el.removeAttribute('data-musk-was-disabled')
      })
    },
  }

  // ---- Concern 3: 交互禁用样式 (InteractionDisabler) ----
  const interactionStylesConcern: EditorConcern = {
    name: 'interaction-styles',
    enable() { disabler.enable() },
    pause() { disabler.disable() },
    resume() { disabler.enable() },
    destroy() { disabler.disable() },
  }

  // ---- Concern 4: 公式编辑 (MathLive) ----
  const formulaConcern: EditorConcern = {
    name: 'formula',
    enable() {
      if (featureConfig.formulaEdit) {
        formulaManager.init().then((ready) => {
          if (ready) {
            log('公式编辑功能已就绪 (MathLive)')
            bridge.send('FORMULA_READY', { formulaCount: formulaManager.scanFormulas() })
          }
        })
      }
    },
    pause() { /* formulaManager.hide() 已由 panelsConcern 处理 */ },
    resume() { /* MathLive 实例保持在内存中,无需重新初始化 */ },
    destroy() { /* formulaManager.destroy() 已由 panelsConcern 处理 */ },
  }

  // ---- Concern 5: contenteditable 属性管理 ----
  const contenteditableConcern: EditorConcern = {
    name: 'contenteditable',
    enable() { /* editManager 在编辑时动态添加 contenteditable */ },
    pause() {
      document.querySelectorAll('[contenteditable="true"]').forEach((el) => {
        el.removeAttribute('contenteditable')
      })
    },
    resume() { /* editManager 会在编辑时重新添加 */ },
    destroy() {
      document.querySelectorAll('[contenteditable="true"]').forEach((el) => {
        el.removeAttribute('contenteditable')
      })
    },
  }

  // ---- Concern 6: 事件拦截器 ----
  const eventInterceptorsConcern: EditorConcern = {
    name: 'event-interceptors',
    enable() {
      function isMathLiveElement(target: Element): Element | null {
        return target.closest('math-virtual-keyboard')
          || target.closest('.ML__keyboard')
          || target.closest('math-field')
      }

      // 点击事件 (在捕获阶段拦截)
      const handleClick: EventListener = (e) => {
        const target = (e as MouseEvent).target as Element
        if (target.closest(`[${CONFIG.TOOLBAR_ATTR}]`)) return
        if (featureConfig.formulaEdit && isMathLiveElement(target)) {
          // 命中 MathLive 时同时消费一次 pointerdown 标记，避免它残留误伤后续 click
          consumeMathLivePointerDownMark()
          return
        }
        // pointerdown 兜底：MathLive 切换 keyboard tab(123/abc/αβγ)时键盘高度变化,
        // 抬起位置已不在键盘上, click.target 退化为 body, 此时 isMathLiveElement 失效。
        // 若最近一次 pointerdown 命中过 MathLive,则视作 MathLive click,避免误关公式面板/键盘。
        if (featureConfig.formulaEdit && consumeMathLivePointerDownMark()) {
          log('点击事件: pointerdown 兜底命中 MathLive, 跳过关闭逻辑')
          return
        }

        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()

        log('点击事件触发', {
          target: target.tagName,
          editable: target.getAttribute(CONFIG.EDITABLE_ATTR),
        })

        function hideAllPanels(): void {
          formulaManager.hide()
          generalPanel.hide()
          imagePanel.hide()
          document.querySelectorAll('.musk-general-editing').forEach((el) => {
            el.classList.remove('musk-general-editing')
          })
          document.querySelectorAll('.musk-formula-editing').forEach((el) => {
            el.classList.remove('musk-formula-editing')
          })
        }

        if (featureConfig.formulaEdit) {
          const formulaEl = formulaManager.findFormulaElement(target)
          if (formulaEl) {
            editManager.stopEditing()
            hideAllPanels()
            formulaManager.show(formulaEl as HTMLElement)
            return
          }
          if (formulaManager.visible) formulaManager.hide()
        }

        // 图片元素
        const imgTarget = target.tagName === 'IMG' ? target : target.closest('img')
        if (imgTarget && imgTarget.hasAttribute('data-musk-element-id')) {
          log('找到图片元素', { src: ((imgTarget as HTMLImageElement).src || '').substring(0, 80) })
          editManager.stopEditing()
          hideAllPanels()
          imagePanel.show(imgTarget as HTMLElement, null as any)
          return
        }

        const editableTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`)
        if (editableTarget) {
          log('找到可编辑元素', {
            tag: editableTarget.tagName,
            class: editableTarget.className,
            text: editableTarget.textContent ? editableTarget.textContent.substring(0, 50) : '',
          })
          hideAllPanels()
          editManager.startEditing(editableTarget as HTMLElement)
          return
        }

        // 通用元素
        const genTarget = findGeneralElement(target)
        if (genTarget && featureConfig.aiEdit) {
          log('找到通用元素', { tag: genTarget.tagName, id: genTarget.getAttribute('data-musk-element-id') })
          editManager.stopEditing()
          hideAllPanels()
          generalPanel.show(genTarget as HTMLElement, null as any)
          return
        }

        log('点击非可编辑区域,结束当前编辑')
        editManager.stopEditing()
        hideAllPanels()
      }
      document.addEventListener('click', handleClick, true)
      eventHandlers.push({ element: document, event: 'click', handler: handleClick, options: true })

      // mousedown 事件 (捕获阶段)
      const handleMouseDown: EventListener = (e) => {
        const target = (e as MouseEvent).target as Element
        if (target.closest(`[${CONFIG.TOOLBAR_ATTR}]`)) return
        if (featureConfig.formulaEdit && (isMathLiveElement(target) || formulaManager.findFormulaElement(target))) return
        const imgEl = target.tagName === 'IMG' ? target : target.closest('img')
        if (imgEl && imgEl.hasAttribute('data-musk-element-id')) return
        const editableTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`) as HTMLElement | null
        if (editableTarget) {
          if (editableTarget !== editManager.getCurrentElement()) {
            editManager.startEditing(editableTarget)
          }
          return
        }
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
      }
      document.addEventListener('mousedown', handleMouseDown, true)
      eventHandlers.push({ element: document, event: 'mousedown', handler: handleMouseDown, options: true })

      // pointerdown 事件 (捕获阶段) - 只阻止不可编辑元素
      const handlePointerDown: EventListener = (e) => {
        const target = (e as PointerEvent).target as Element
        if (target.closest(`[${CONFIG.TOOLBAR_ATTR}]`)) return
        if (featureConfig.formulaEdit && (isMathLiveElement(target) || formulaManager.findFormulaElement(target))) {
          // 打标记：紧随的 click 即使 target 退化为 body,也能识别为 MathLive 事件
          if (isMathLiveElement(target)) markMathLivePointerDown()
          return
        }
        const editableTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`)
        if (editableTarget) return
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
      }
      document.addEventListener('pointerdown', handlePointerDown, true)
      eventHandlers.push({ element: document, event: 'pointerdown', handler: handlePointerDown, options: true })

      // 通用的 preventDefault 处理器
      const preventDefault: EventListener = (e) => {
        e.preventDefault()
        e.stopPropagation()
      }

      // 禁用表单提交
      document.addEventListener('submit', preventDefault, true)
      eventHandlers.push({ element: document, event: 'submit', handler: preventDefault, options: true })

      // 禁用触摸事件 (移动端)
      document.addEventListener('touchstart', preventDefault, true)
      eventHandlers.push({ element: document, event: 'touchstart', handler: preventDefault, options: true })
      document.addEventListener('touchend', preventDefault, true)
      eventHandlers.push({ element: document, event: 'touchend', handler: preventDefault, options: true })

      // 禁用拖拽
      document.addEventListener('dragstart', preventDefault, true)
      eventHandlers.push({ element: document, event: 'dragstart', handler: preventDefault, options: true })

      // 禁用右键菜单 (对不可编辑元素)
      const handleContextMenu: EventListener = (e) => {
        const target = (e as MouseEvent).target as Element
        const editableTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`)
        if (!editableTarget) {
          e.preventDefault()
        }
      }
      document.addEventListener('contextmenu', handleContextMenu, true)
      eventHandlers.push({ element: document, event: 'contextmenu', handler: handleContextMenu, options: true })

      // 快捷键支持
      const handleKeyDown: EventListener = (e) => {
        const target = (e as KeyboardEvent).target as HTMLElement | null
        if (target && target.isContentEditable) {
          log('在可编辑元素中，允许浏览器原生撤销/重做')
        }
      }
      document.addEventListener('keydown', handleKeyDown, true)
      eventHandlers.push({ element: document, event: 'keydown', handler: handleKeyDown, options: true })

      // 监听失去焦点
      const handleFocusOut: EventListener = (e) => {
        const fe = e as FocusEvent
        const target = fe.target as HTMLElement | null
        const relatedTarget = fe.relatedTarget as HTMLElement | null
        if (relatedTarget?.closest?.(`[${CONFIG.TOOLBAR_ATTR}]`)) {
          return
        }
        if (relatedTarget && featureConfig.formulaEdit && isMathLiveElement(relatedTarget)) {
          return
        }
        if (target && target.isContentEditable && target === editManager.getCurrentElement()) {
          editManager.stopEditing()
        }
      }
      document.addEventListener('focusout', handleFocusOut, true)
      eventHandlers.push({ element: document, event: 'focusout', handler: handleFocusOut, options: true })

      // 监听 mouseover/mouseout 来动态添加/移除 hover 样式
      let currentHoverElement: Element | null = null
      let currentFormulaHover: Element | null = null
      let currentGeneralHover: Element | null = null

      function clearAllHovers(): void {
        if (currentHoverElement) { currentHoverElement.classList.remove('musk-editor-hover'); currentHoverElement = null }
        if (currentFormulaHover) { currentFormulaHover.classList.remove('musk-formula-hover'); currentFormulaHover = null }
        if (currentGeneralHover) { currentGeneralHover.classList.remove('musk-general-hover'); currentGeneralHover = null }
      }

      const handleMouseOver: EventListener = (e) => {
        const target = (e as MouseEvent).target as Element

        if (featureConfig.formulaEdit) {
          const fEl = formulaManager.findFormulaElement(target)
          if (fEl) {
            if (fEl !== currentFormulaHover) {
              clearAllHovers()
              if (!fEl.classList.contains('musk-formula-editing')) fEl.classList.add('musk-formula-hover')
              currentFormulaHover = fEl
            }
            return
          }
        }
        const textTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`)
        if (textTarget) {
          if (textTarget !== currentHoverElement) {
            clearAllHovers()
            if (!textTarget.classList.contains('musk-editor-editing')) textTarget.classList.add('musk-editor-hover')
            currentHoverElement = textTarget
          }
          return
        }
        const imgHover = target.tagName === 'IMG' ? target : target.closest('img')
        if (imgHover && imgHover.hasAttribute('data-musk-element-id')) {
          if (imgHover !== currentGeneralHover) {
            clearAllHovers()
            if (!imgHover.classList.contains('musk-general-editing')) imgHover.classList.add('musk-general-hover')
            currentGeneralHover = imgHover
          }
          return
        }
        if (featureConfig.aiEdit) {
          const genTarget = findGeneralElement(target)
          if (genTarget) {
            if (genTarget !== currentGeneralHover) {
              clearAllHovers()
              if (!genTarget.classList.contains('musk-general-editing')) genTarget.classList.add('musk-general-hover')
              currentGeneralHover = genTarget
            }
            return
          }
        }
        if (currentFormulaHover) { currentFormulaHover.classList.remove('musk-formula-hover'); currentFormulaHover = null }
        if (currentGeneralHover) { currentGeneralHover.classList.remove('musk-general-hover'); currentGeneralHover = null }
      }

      const handleMouseOut: EventListener = (e) => {
        const me = e as MouseEvent
        const target = me.target as Element
        const related = me.relatedTarget as Element | null

        if (featureConfig.formulaEdit && currentFormulaHover) {
          const fEl = formulaManager.findFormulaElement(target)
          if (fEl && fEl === currentFormulaHover) {
            if (!fEl.contains(related) && (!related || !formulaManager.findFormulaElement(related))) {
              fEl.classList.remove('musk-formula-hover')
              currentFormulaHover = null
            }
          }
        }
        if (currentHoverElement) {
          const editableTarget = target.closest(`[${CONFIG.EDITABLE_ATTR}="true"]`)
          if (editableTarget && editableTarget === currentHoverElement && !editableTarget.contains(related)) {
            editableTarget.classList.remove('musk-editor-hover')
            currentHoverElement = null
          }
        }
        if (currentGeneralHover) {
          if (currentGeneralHover === target || currentGeneralHover.contains(target)) {
            if (!currentGeneralHover.contains(related)) {
              currentGeneralHover.classList.remove('musk-general-hover')
              currentGeneralHover = null
            }
          }
        }
      }

      document.addEventListener('mouseover', handleMouseOver, true)
      eventHandlers.push({ element: document, event: 'mouseover', handler: handleMouseOver, options: true })
      document.addEventListener('mouseout', handleMouseOut, true)
      eventHandlers.push({ element: document, event: 'mouseout', handler: handleMouseOut, options: true })

      log('所有交互事件已注册')
    },
    pause() {
      eventHandlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options)
      })
      log('已移除事件拦截器 (暂停)')
    },
    resume() {
      eventHandlers = []
      this.enable()
    },
    destroy() {
      eventHandlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options)
      })
      eventHandlers = []
      log('已移除所有事件监听器')
    },
  }

  return [
    panelsConcern,
    disabledAttrConcern,
    interactionStylesConcern,
    formulaConcern,
    contenteditableConcern,
    eventInterceptorsConcern,
  ]
}

// ========== init ==========

function init(): void {
  log('开始初始化编辑器...')

  // 1. 标记可编辑元素 (只在第一次标记,避免重复标记导致属性被覆盖)
  const alreadyMarked = document.querySelector('[data-musk-element-id]')

  if (!alreadyMarked) {
    const result = markAllElements()
    log('首次标记元素完成', { editable: result.editableCount, nonEditable: result.nonEditableCount, total: result.total })

    // 保存所有可编辑元素的初始文本 (用于 RESTORE)
    const editableElements = document.querySelectorAll(`[${CONFIG.EDITABLE_ATTR}="true"]`)
    editableElements.forEach((element) => {
      const elementId = element.getAttribute('data-musk-element-id')
      if (elementId) {
        history.saveInitialText(elementId, element.textContent || '')
      }
      // Safari: button 默认不接受鼠标点击聚焦，设置 tabIndex 使其可聚焦
      if (element.tagName === 'BUTTON') {
        element.setAttribute('tabindex', '0')
      }
    })
    log('已保存初始文本', { count: editableElements.length })
  } else {
    log('元素已标记,跳过重复标记')
  }

  // 2. 启用所有编辑关注点
  concerns.forEach((c) => c.enable())

  // 3. 监听父窗口指令 (bridge.on 使用 Map.set，幂等注册)
  bridge.on('UNDO', () => {
    editManager.stopEditing()
    // 撤销时不应让 hide 触发副作用提交（图片改动 / 手动公式自动应用），
    // 否则会先把"未提交的编辑"加进 history，再撤销，看起来像撤销失效
    formulaManager.hide(false)
    generalPanel.hide()
    imagePanel.hide(false)
    toolbar.hide()
    history.undo()
  })

  bridge.on('REDO', () => {
    editManager.stopEditing()
    formulaManager.hide(false)
    generalPanel.hide()
    imagePanel.hide(false)
    toolbar.hide()
    history.redo()
  })

  bridge.on('SAVE', async () => {
    editManager.stopEditing()

    try {
      await imagePanel.waitForPendingUploads()
      formulaManager.hide()
      generalPanel.hide()
      imagePanel.hide()
      toolbar.hide()

      const cleanedHtml = cleanHtmlForSave()

      bridge.send('SAVE_COMPLETE', {
        content: cleanedHtml,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : '图片上传失败，请重新保存'
      bridge.send('SAVE_FAILED', { message })
    }
  })

  bridge.on('EXIT', () => {
    editManager.stopEditing()
    formulaManager.hide()
    generalPanel.hide()
    imagePanel.hide()
    toolbar.hide()
    disabler.disable()
  })

  bridge.on('IMAGE_UPLOAD_RESULT', (payload) => {
    log('收到图片上传结果', payload)
    imagePanel.handleUploadResult(payload)
  })

  bridge.on('AI_POLISH_TEXT_RESULT', (payload) => {
    log('收到 AI 润色结果', payload)
    if (payload && payload.mode === 'formula') {
      formulaManager.handleAIFormulaResult(payload as any)
    } else {
      toolbar.handleAITextResult(payload as any)
    }
  })

  bridge.on('AI_IMAGE_RESULT', (payload) => {
    log('收到 AI 生图结果', payload)
    imagePanel.handleAIImageResult(payload)
  })

  // 恢复到原始状态 (通过 initialTextMap 恢复)
  bridge.on('RESTORE', () => {
    log('收到 RESTORE 命令,恢复所有元素到初始状态')
    editManager.stopEditing()
    formulaManager.hide()
    generalPanel.hide()
    imagePanel.hide()
    toolbar.hide()
    history.restoreAll()
    log('恢复完成')
  })

  bridge.on('HIDE_PANELS', (payload) => {
    // payload.silent === true 时，hide 不触发副作用提交（用于 PPT 模式 UNDO/REDO 转发场景）
    const silent = !!(payload && (payload as any).silent)
    editManager.stopEditing()
    formulaManager.hide(!silent)
    generalPanel.hide()
    imagePanel.hide(!silent)
    toolbar.hide()
  })

  bridge.on('GET_STATS', () => {
    bridge.send('STATS', {
      historyLength: (history as any).operations.length,
      currentIndex: (history as any).currentIndex,
      isEditing: editManager.isEditing(),
    })
  })

  // 4. 通知父窗口就绪
  bridge.send('EDITOR_READY', {
    version: '1.3.10',
    canUndo: history.canUndo(),
    canRedo: history.canRedo(),
    features: featureConfig,
    mathJaxReady: (formulaManager as any).mathJaxReady,
    timestamp: Date.now(),
  })

  log('编辑器初始化完成')
}

// ========== enableEditor / disableEditor ==========

function enableEditor(features?: Record<string, boolean>): void {
  if (isEditorEnabled) return
  isEditorEnabled = true

  if (features && typeof features === 'object') {
    featureConfig = { ...CONFIG.FEATURES, ...features } as FeatureConfig
  }

  // Sync featureConfig to all instances that captured it at construction time
  (toolbar as any).featureConfig = featureConfig;
  (formulaManager as any).featureConfig = featureConfig;
  (editManager as any).featureConfig = featureConfig

  // 构建关注点（每次 enable 重新构建，确保闭包捕获最新 featureConfig）
  concerns.length = 0
  buildConcerns().forEach((c) => concerns.push(c))

  log('启用编辑器...', { features: featureConfig, concerns: concerns.map((c) => c.name) })
  init()
}

function disableEditor(): void {
  if (!isEditorEnabled) return
  isEditorEnabled = false

  log('禁用编辑器...')
  // 反向 destroy：让事件拦截器（数组末位）最先停止，避免 panel.destroy 期间
  // 因残留 click/mousedown 事件触发 *.show() 重新创建面板。
  for (let i = concerns.length - 1; i >= 0; i--) {
    try {
      concerns[i].destroy()
    } catch (e) {
      log(`concern ${concerns[i].name} destroy 失败`, e)
    }
  }
  concerns.length = 0

  // 兜底清扫：扫描所有带编辑器 toolbar 标记的 DOM 节点强制移除，
  // 防御性兜底——即使上面任何 panel 的 destroy 漏了 / 失败了 / 期间又被
  // 重建，这一步都能保证编辑模式 UI 副作用 0 残留。
  document.querySelectorAll(`[${CONFIG.TOOLBAR_ATTR}]`).forEach((el) => el.remove())

  log('编辑器已完全禁用')
}

// ========== pauseEditor / resumeEditor (preview sub-mode) ==========

let isEditorPaused = false

function pauseEditor(): void {
  if (!isEditorEnabled || isEditorPaused) return
  isEditorPaused = true

  log('暂停编辑器 (进入预览子模式)...')
  // 反向 pause：让事件拦截器最先停止，避免 panel hide 后被残留事件重新触发 show
  for (let i = concerns.length - 1; i >= 0; i--) {
    try {
      concerns[i].pause()
    } catch (e) {
      log(`concern ${concerns[i].name} pause 失败`, e)
    }
  }
  log('编辑器已暂停, HTML 交互已恢复')
}

function resumeEditor(): void {
  if (!isEditorEnabled || !isEditorPaused) return
  isEditorPaused = false

  log('恢复编辑器 (退出预览子模式)...')
  // 补标记预览期间动态创建的新 DOM（如弹窗内容）
  markNewElements()
  concerns.forEach((c) => c.resume())
  log('编辑器已恢复')
}

// ========== initEditor ==========

export default function initEditor(): void {
  if (isInitialized) return
  isInitialized = true

  log('编辑器已加载,等待 ENABLE_EDIT 消息...')

  bridge.on('ENABLE_EDIT', (payload) => {
    log('收到 ENABLE_EDIT 消息', payload)
    enableEditor(payload?.features as Record<string, boolean> | undefined)
  })

  bridge.on('DISABLE_EDIT', () => {
    log('收到 DISABLE_EDIT 消息')
    disableEditor()
  })

  bridge.on('PAUSE_EDIT', () => {
    log('收到 PAUSE_EDIT 消息')
    pauseEditor()
  })

  bridge.on('RESUME_EDIT', () => {
    log('收到 RESUME_EDIT 消息')
    resumeEditor()
  })

  bridge.send('EDITOR_READY', {
    canUndo: false,
    canRedo: false,
    isEditorEnabled: false,
  })
}

// ========== Debug API ==========

export function getDebugApi(): Record<string, unknown> {
  return {
    version: '1.3.10',
    history,
    checker: { markAllElements },
    editManager,
    disabler,
    toolbar,
    formulaManager,
    generalPanel,
    imagePanel,
    bridge,
    enableEditor,
    disableEditor,
    waitForPendingImageUploads: () => imagePanel.waitForPendingUploads(),
    getFeatures: () => ({ ...featureConfig }),
    getStats: () => ({
      canUndo: history.canUndo(),
      canRedo: history.canRedo(),
      historyLength: (history as any).operations.length,
      currentIndex: (history as any).currentIndex,
      isEditing: editManager.isEditing(),
      isEditorEnabled,
      mathJaxReady: (formulaManager as any).mathJaxReady,
      features: { ...featureConfig },
    }),
    undo: () => history.undo(),
    redo: () => history.redo(),
    save: () => bridge.send('SAVE', {}),
  }
}
